// @flow
import React from "react";
import shallowCompare from "react-addons-shallow-compare";
import { connect } from "react-redux";
import Sticky from "react-stickynode";
import Waypoint from "react-waypoint";
import BookmarkButtonAlt from "backpack-ui/dist/components/bookmarkButtonAlt";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import PageHeader from "./pageHeader";
import EssentialInfo from "./essentialInfo";
import ContentHeader from "./contentHeader";
import { connected as NearbyPlaces } from "./nearbyPlaces";
import Callout from "./callout";
import EditLink from "./editLink";
import ImagesContainer from "./imagesContainer";
import ShareMenu from "./shareMenu";
import ContactBar from "./contactBar";
import { connected as Booking } from "./booking";
import styles from "./styles";
import { createListUrl, createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";
import editUrl from "../utils/editUrl";
import cleanUp from "../utils/cleanUp";
import type {
  Poi, Place, Breadcrumb, MobileState,
} from "../../../../server/types";

export type TransportPoiComponentProps = {
  poi: Poi,
  place: Place,
  images: Array<string>,
  breadcrumb: Array<Breadcrumb>,
  mobile: MobileState,
  params: Object,
  onClickBookmark: Function,
  marked: Boolean,
};

/**
 * Renders a bookable activity POI
 */
class TransportPoiComponent extends React.Component {
  constructor() {
    super();

    this.state = {
      nearby: false,
      hideMap: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.enterNearbyPlaces = this.enterNearbyPlaces.bind(this);
    this.leaveNearbyPlaces = this.leaveNearbyPlaces.bind(this);
  }

  state: {
    nearby: boolean,
    hideMap: boolean,
  };

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl: () => string;

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(poi, breadcrumb.map(b => b.title), params.placeId);
  }

  props: TransportPoiComponentProps;
  enterNearbyPlaces: () => void;
  leaveNearbyPlaces: () => void;

  enterNearbyPlaces() {
    this.setState({
      hideMap: true,
    });
  }

  leaveNearbyPlaces(props) {
    this.setState({
      hideMap: (props.currentPosition === "above"),
    });
  }

  render() {
    const { poi, breadcrumb, place, images, params } = this.props;
    const subtype = poiTypes[poi.poiType.toLowerCase()].displayName;

    if (poi.review) {
      Object.keys(poi.review).forEach((review) => {
        poi.review[review] = cleanUp.removeBackslash(poi.review[review]);
      });
    }

    const tinyHeading = {
      level: 4,
      size: "tiny",
      weight: "thick",
      importance: "high",
      caps: true,
    };

    const name = poi.name;

    const poiId = `poi-${poi.id}`;
    const categoryLink = createListUrl({
      resource: poi.type,
      kind: poi.poiType,
      breadcrumb: breadcrumb.map(b => b.title),
      placeId: place.id,
    });

    const currentBreadcrumb = {
      title: subtype,
      href: categoryLink,
    };

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <Breadcrumbs
          links={[...breadcrumb, currentBreadcrumb]}
        />

        <div className="Page Page--detail">
          <PageHeader
            heading={name}
            title={`${place.name} ${subtype}`}
            titleHref={categoryLink}
            topChoice={poi.poiAttributes && poi.poiAttributes.length &&
              poi.poiAttributes[0].name === "top_choice"}
            type={poi.subtypes[0]}
            place={poi.containingPlace.name}
            alignment="center"
            contained
          />

          <div className="PageContent">
            {this.props.marked !== null &&
              <div>
                <BookmarkButtonAlt
                  className="js-gtm-bookmark-button"
                  onClick={this.props.onClickBookmark}
                  marked={this.props.marked}
                  style={styles.bookmarkButtonAlt}
                />
                <FeatureDiscover
                  featureName="firstPoiSeen"
                  removeOnOuterClick
                  removeOnNavigate
                >
                  <DiscoveryFlyout
                    headline="Save this place"
                    description="Tap the bookmark to save and organize your interests into lists."
                    style={styles.featureDiscovery.button}
                  />
                </FeatureDiscover>
              </div>
            }

            {images && images.length > 0 && this.props.mobile &&
              <ImagesContainer images={images} mobile />
            }

            <aside className="PageAside" data-hide-map={this.state.hideMap}>
              <Sticky
                enabled={!this.props.mobile}
                top={30}
                bottomBoundary={`#${poiId} .PageContent`}
              >
                {!this.props.mobile && false &&
                  <Booking
                    type="activity"
                    price={{
                      amount: 49,
                      currency: "USD",
                      rate: "per person",
                    }}
                  />
                }

                <EssentialInfo
                  poi={poi}
                  place={place}
                  mobile={this.props.mobile}
                  links={breadcrumb}
                />

                {(poi.telephone || poi.url) &&
                  <ContactBar
                    phone={poi.telephone}
                    website={poi.url}
                  />
                }

                {this.props.mobile && false &&
                  <Booking
                    type="activity"
                    price={{
                      amount: 49,
                      currency: "USD",
                      rate: "per person",
                    }}
                  />
                }
              </Sticky>
            </aside>

            <article className="PageArticle">
              {images && images.length > 0 && !this.props.mobile &&
                <ImagesContainer images={images} />
              }

              <div className="PageArticle-content">
                <article>
                  {poi.review && poi.review.essential &&
                    <ContentBlock
                      heading=""
                      htmlContent={poi.review.essential}
                    />
                  }

                  {poi.review && poi.review.extension &&
                    <ContentBlock
                      heading=""
                      htmlContent={poi.review.extension}
                    />
                  }

                  <footer className="ContentFooter">
                    <div className="ContentFooter-text">
                      <EditLink url={editUrl(poi, place, breadcrumb)} />
                    </div>

                    <div className="ContentFooter-share">
                      <ShareMenu
                        text={poi.name}
                        url={`https://www.lonelyplanet.com${this.getPoiUrl()}`}
                        mobile={this.props.mobile}
                      />
                    </div>
                  </footer>
                </article>

                <aside>
                  {false &&
                    <Callout
                      type="book"
                      align="center"
                      heading="Lonely Planet’s Best in Travel 2016"
                      slug="/"
                      price={{
                        currency: "USD",
                        amount: 21.99,
                      }}
                      description={`Be an in-the-know traveler this year with Lonely
                        Planet’s collection of the hottest trends, destinations,
                        journeys.`}
                      image="http://dummyimage.com/132x168/4d494d/686a82.gif"
                    />
                  }

                  {poi.location &&
                    <section className="ContentSection ContentSection-nearbyPlaces">
                      <ContentHeader
                        title="Location &amp; nearby places"
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <NearbyPlaces
                        currentPlace={poi}
                        mapId={`leaflet-map-container-${poi.id}`}
                        relatedPlaceId={params.placeId}
                      />

                      <Waypoint
                        onEnter={this.enterNearbyPlaces}
                        onLeave={this.leaveNearbyPlaces}
                        fireOnRapidScroll
                      />
                    </section>
                  }
                </aside>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

TransportPoiComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: React.PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: React.PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: React.PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: React.PropTypes.bool,

  /**
   * React router params
   */
  params: React.PropTypes.object,
  onClickBookmark: React.PropTypes.func,
  marked: React.PropTypes.bool,
};

const mapStateToProps = (state) => ({
  mobile: state.mobile.isMobile,
});

export default connect(mapStateToProps)(TransportPoiComponent);



// WEBPACK FOOTER //
// ./assets/pois/components/transport.jsx